import { useState, useEffect, useCallback } from 'react';
import { getImageUrl } from '../lib/api';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

export default function ImageGalleryModal({ images, initialIndex = 0, onClose }) {
  const [current, setCurrent] = useState(initialIndex);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') setCurrent(p => Math.max(0, p - 1));
    if (e.key === 'ArrowRight') setCurrent(p => Math.min(images.length - 1, p + 1));
  }, [images.length, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  if (!images || images.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="image-gallery-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90" onClick={onClose} />

      {/* Close */}
      <button onClick={onClose} className="absolute top-6 right-6 z-10 text-white/70 hover:text-white transition-colors" data-testid="gallery-close-btn">
        <X size={28} />
      </button>

      {/* Counter */}
      <div className="absolute top-6 left-6 z-10 text-white/70 text-sm font-sans">
        {current + 1} / {images.length}
      </div>

      {/* Image */}
      <div className="relative z-10 max-w-[85vw] max-h-[85vh] flex items-center justify-center">
        <img
          src={getImageUrl(images[current])}
          alt={`Image ${current + 1}`}
          className="max-w-full max-h-[80vh] object-contain rounded-sm shadow-2xl"
          data-testid="gallery-main-image"
        />
      </div>

      {/* Navigation */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent(p => Math.max(0, p - 1)); }}
            disabled={current === 0}
            className="absolute left-6 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white disabled:opacity-20 transition-all"
            data-testid="gallery-prev-btn"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent(p => Math.min(images.length - 1, p + 1)); }}
            disabled={current === images.length - 1}
            className="absolute right-6 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white disabled:opacity-20 transition-all"
            data-testid="gallery-next-btn"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="absolute bottom-6 z-10 flex gap-2 overflow-x-auto max-w-[80vw] p-2">
          {images.map((img, idx) => (
            <button
              key={`${img}-${idx}`}
              onClick={(e) => { e.stopPropagation(); setCurrent(idx); }}
              className={`w-16 h-16 rounded-sm overflow-hidden border-2 shrink-0 transition-all ${idx === current ? 'border-[#C5A059] opacity-100' : 'border-transparent opacity-50 hover:opacity-75'}`}
              data-testid={`gallery-thumb-${idx}`}
            >
              <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Trigger component to open gallery from a product image
export function GalleryTrigger({ coverImage, galleryImages = [], className = '' }) {
  const [open, setOpen] = useState(false);
  const allImages = [coverImage, ...galleryImages].filter(Boolean);

  if (allImages.length === 0) return <div className={`img-placeholder ${className}`}>N/A</div>;

  return (
    <>
      <div className={`relative group cursor-pointer ${className}`} onClick={() => setOpen(true)} data-testid="gallery-trigger">
        <img src={getImageUrl(allImages[0])} alt="" className="w-full h-full object-cover" />
        {allImages.length > 1 && (
          <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded-sm">
            +{allImages.length - 1}
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <ZoomIn size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      {open && <ImageGalleryModal images={allImages} onClose={() => setOpen(false)} />}
    </>
  );
}
